import {
	closestCenter,
	DndContext,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { Icon, IconButton } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../../api';
import { useRefs } from '../../../hooks/useRefs';
import { NewTeamModal } from '../../NewTeamModal/NewTeamModal';
import { SideBarCategoryToggle } from './SideBarCategoryToggle';
import SideBarLink from './SideBarLink';
import type { TeamSideBarItemHandle } from './TeamSideBarItem';
import { TeamSideBarItem } from './TeamSideBarItem';
import { useTeamSideBarItemStyle } from './TeamSideBarItem.styles';

interface SideBarTeamsLinksProps {
	teamOrder: string[];
	updateTeamOrder: (newTeams: string[]) => void;
}

function SideBarTeamsLinks({
	teamOrder,
	updateTeamOrder,
}: SideBarTeamsLinksProps) {
	const [
		createTeamModalOpened,
		{ close: closeCreateTeamModal, open: openCreateTeamModal },
	] = useDisclosure(false);

	const { isAdminUser, isGuestUser } = useAuthUser();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	);

	const { data: teams } = useApiListTeamsV2({
		queryParams: {
			only_joined: true,
		},
	});

	const orderedTeams = useMemo(() => {
		const teamsToDisplay = teams || [];

		const orderedTeamsToDisplay = teamOrder
			.map((teamId) =>
				teamsToDisplay.find((team: TeamOut) => team.id === teamId)
			)
			.filter(Boolean) as TeamOut[];

		const remainingTeams = teamsToDisplay.filter(
			(team: TeamOut) => !teamOrder.includes(team.id)
		);

		return [...orderedTeamsToDisplay, ...remainingTeams];
	}, [teams, teamOrder]);

	const [localTeamOrder, setLocalTeamOrder] = useState<TeamOut[]>([]);

	// Merge localTeamOrder with any new teams from orderedTeams
	const currentLocalTeamOrder = useMemo(() => {
		if (localTeamOrder.length === 0) {
			return orderedTeams;
		}

		// Filter localTeamOrder to only include teams still in orderedTeams
		const orderedTeamIds = orderedTeams.map((team) => team.id);
		const filteredLocalTeams = localTeamOrder.filter((team) =>
			orderedTeamIds.includes(team.id)
		);

		// Keep the local order but append any new teams not in localTeamOrder
		const localTeamIds = filteredLocalTeams.map((team) => team.id);
		const newTeams = orderedTeams.filter(
			(team) => !localTeamIds.includes(team.id)
		);

		return [...filteredLocalTeams, ...newTeams];
	}, [localTeamOrder, orderedTeams]);

	const { classes, cx } = useTeamSideBarItemStyle({
		open: true,
	});

	const { refs: teamMenuRefs, setRefFromKey } =
		useRefs<TeamSideBarItemHandle>();
	const navigate = useNavigate();

	const onTeamCreationFinish = (teamId: string) => {
		const teamMenuRef = teamMenuRefs[teamId];
		if (teamMenuRef) {
			teamMenuRef.open();
		}

		navigate(`/teams/${teamId}/`);
	};

	const handleTeamOrderChange = useCallback(
		(newTeams: TeamOut[]) => {
			const newTeamIds = newTeams.map((team) => team.id);
			const oldTeams = currentLocalTeamOrder;
			setLocalTeamOrder(newTeams);
			try {
				updateTeamOrder(newTeamIds);
			} catch {
				showNotification({
					title: 'Failed to save team order',
					message:
						'Your team order changes could not be saved. Please try again.',
				});
				setLocalTeamOrder(oldTeams);
			}
		},
		[updateTeamOrder, currentLocalTeamOrder]
	);

	const handleDragEnd = useCallback(
		({ active, over }: { active: any; over: any }) => {
			if (active.id !== over?.id) {
				const oldIndex = currentLocalTeamOrder.findIndex(
					(team) => team.id === active.id
				);
				const newIndex = currentLocalTeamOrder.findIndex(
					(team) => team.id === over?.id
				);
				if (oldIndex !== -1 && newIndex !== -1) {
					const newTeams = arrayMove(currentLocalTeamOrder, oldIndex, newIndex);
					handleTeamOrderChange(newTeams);
				}
			}
		},
		[currentLocalTeamOrder, handleTeamOrderChange]
	);

	return (
		<Stack spacing={0}>
			<NewTeamModal
				opened={createTeamModalOpened}
				onClose={closeCreateTeamModal}
				onFinish={onTeamCreationFinish}
			/>
			<SideBarCategoryToggle
				label="Teams"
				labelRightSection={
					isAdminUser && (
						<IconButton
							onClick={openCreateTeamModal}
							className={cx(classes.hiddenButton, classes.displayOnHover)}
							iconName="plus"
							variant="tertiary"
							size="sm"
							data-testid="sidebar-create-team-button"
							tooltip="Create new team"
							tooltipProps={{ position: 'right', withArrow: true }}
							mr="4xs"
						/>
					)
				}
				toggleKey="your-teams-sidebar-collapsable-opened"
			>
				<Stack spacing={0} align="flex-start">
					<Box pb="5xs" w="100%">
						<SideBarLink
							link={{
								id: 'nav::teams',
								label: 'Browse teams',
								icon: <Icon name="users" color="icon/primary/default" />,
								href: ['/teams'],
								matchExact: true,
								show: !isGuestUser,
							}}
							selected={location.pathname === '/teams'}
						/>
					</Box>
					<Box w="100%">
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToVerticalAxis, restrictToParentElement]}
						>
							<SortableContext
								items={currentLocalTeamOrder.map((team) => team.id)}
								strategy={verticalListSortingStrategy}
							>
								{currentLocalTeamOrder.map((team) => (
									<TeamSideBarItem
										ref={setRefFromKey(team.id)}
										key={team.id}
										team={team}
									/>
								))}
							</SortableContext>
						</DndContext>
					</Box>
				</Stack>
			</SideBarCategoryToggle>
		</Stack>
	);
}

const ObservedSideBarTeamsLinks = observer(SideBarTeamsLinks);
export default ObservedSideBarTeamsLinks;
